import { Link } from "react-router-dom";
import { Navigation, Footer, ScriptureBlock } from "@/components/Layout";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { ScrollReveal } from "@/hooks/useScrollReveal";

const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_truth-centered/artifacts/sxl0iq14_DB20D98A-90BA-4B88-994D-95E4465CDC7A.jpeg";

const values = [
  {
    title: "Integrity",
    description: "We do what we say. Our word is our bond, because we answer to God for every promise made to land sellers and builders."
  },
  {
    title: "Wisdom",
    description: "We seek understanding before action. Quick decisions come from deep preparation and prayer."
  },
  {
    title: "Stewardship",
    description: "We manage what isn't ours. Every resource, every relationship, every land opportunity belongs to God."
  },
  {
    title: "Faith",
    description: "We depend on God for outcomes. Our job is faithfulness in land stewardship; results are His."
  }
];

export default function About() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <SEO 
        title="About Us"
        description="About Steward Lots and founder Mark Pryor Jr. A Christ-led land stewardship business serving land sellers and builders with integrity, transparency, and ethical land wholesaling practices."
        path="/about"
      />
      <Navigation mode="builder" />

      {/* Hero */}
      <section className="pt-24 pb-16 px-6 md:px-12" data-testid="about-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-4xl md:text-5xl text-[#1C1917] mb-6">
            About Steward Lots™
          </h1>
          <p className="text-[#57534E] text-lg max-w-2xl mx-auto">
            A Christ-led land stewardship business founded on Scripture, integrity, and the conviction that Jesus Christ is Lord over all things—including land transactions.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-6 md:px-12" data-testid="mission-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-6 text-center">
            Our Mission: Long-Term Obedience Over Profit
          </h2>
          <div className="text-[#57534E] space-y-4">
            <p>
              Steward Lots™ exists to facilitate land transactions in a way that honors God and serves people well. 
              We connect <Link to="/seller" className="text-[#BFA15F] hover:underline">land sellers</Link> with 
              qualified <Link to="/builder" className="text-[#BFA15F] hover:underline">builders and land buyers</Link> through 
              a process built on transparency, full disclosure, and fair dealing.
            </p>
            <p>
              Our mission is not to maximize profit at any cost. It is to build a sustainable business that 
              demonstrates what ethical land wholesaling looks like when governed by Scripture. We measure 
              success by faithfulness, not just financial metrics. We prioritize long-term obedience to 
              God's commands over short-term gains.
            </p>
            <p>
              This approach may mean walking away from deals that others would pursue. It may mean slower 
              growth than aggressive competitors. But we believe that God honors businesses that operate 
              under His authority, and we trust Him with the results.
            </p>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-16 px-6 md:px-12 bg-[#F5F5F4]" data-testid="founder-section">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-1">
              <div className="w-32 h-32 md:w-full md:h-auto md:aspect-square overflow-hidden bg-[#E7E5E4]">
                <img 
                  src={FOUNDER_IMAGE} 
                  alt="Mark Pryor Jr - Founder of Steward Lots, Christ-led land stewardship business serving Ohio and beyond"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="mt-4 font-medium text-[#1C1917]">Mark Pryor Jr</p>
              <p className="text-sm text-[#57534E]">Founder</p>
            </div>
            <div className="md:col-span-2">
              <h2 className="font-serif text-2xl md:text-3xl text-[#1C1917] mb-6">
                A Word from the Founder
              </h2>
              <div className="space-y-4 text-[#57534E]">
                <p>
                  My heart belongs to God; this work exists to honor Jesus Christ.
                </p>
                <p>
                  I started Steward Lots™ because I saw an industry that desperately needed integrity. Too many land 
                  transactions involve hidden spreads, pressure tactics, and sellers who don't understand what they're 
                  agreeing to. Land wholesaling has earned a bad reputation—and in many cases, it's deserved.
                </p>
                <p>
                  I wanted to build something different—a land facilitation business where Scripture actually shapes 
                  how we operate, not just what we put on our website. Where land sellers receive full disclosure and 
                  fair treatment. Where builders and land buyers get clean deals with transparent economics.
                </p>
                <p>
                  Our <Link to="/faith" className="text-[#BFA15F] hover:underline">Covenant Accountability Model</Link> isn't 
                  a marketing gimmick—it's the framework that governs every decision we make. We would rather lose a 
                  deal than violate God's commands.
                </p>
                <p>
                  Any good fruit from this work is the Lord's doing. I'm just a steward, managing what He's entrusted 
                  to me for however long He allows.
                </p>
                <ScriptureBlock
                  verse="Iron sharpeneth iron; so a man sharpeneth the countenance of his friend."
                  reference="Proverbs 27:17"
                  className="mt-6"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <ScrollReveal animation="reveal-up">
        <section className="py-24 px-6 md:px-12" data-testid="values-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] text-center mb-6">
              Core Values of Our Land Stewardship
            </h2>
            <p className="text-[#57534E] text-lg max-w-2xl mx-auto text-center mb-16">
              These values guide every interaction with land sellers, builders, and land buyers.
            </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <article 
                key={index}
                className="text-center interactive-card p-4"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 bg-[#0F0F10] text-white font-serif text-2xl flex items-center justify-center mx-auto mb-4 transition-colors hover:bg-[#BFA15F]" aria-hidden="true">
                  {value.title[0]}
                </div>
                <h3 className="font-serif text-xl text-[#1C1917] mb-2">{value.title}</h3>
                <p className="text-[#57534E] text-sm">{value.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </ScrollReveal>

      {/* Vision Section */}
      <ScrollReveal animation="reveal-up">
        <section className="py-16 px-6 md:px-12 bg-[#F5F5F4]" data-testid="vision-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-6 text-center">
              Our Vision for Ethical Land Facilitation
            </h2>
            <div className="text-[#57534E] space-y-4">
              <p>
                We envision a land industry where integrity is the norm, not the exception. Where land sellers 
                can trust that they're being told the truth. Where builders and land buyers know they're getting 
                fair deals with disclosed economics. Where Christ-centered businesses demonstrate that profit 
                and principle can coexist.
              </p>
              <p>
                Steward Lots™ is one small piece of this vision. We serve land sellers and land buyers primarily 
                in Ohio, with plans to expand as God allows. But our primary goal is not geographic expansion—it's 
              faithful execution of the model He's given us.
            </p>
            <p>
              Whether you are a <Link to="/seller" className="text-[#BFA15F] hover:underline">land seller seeking honest treatment</Link> or 
              a <Link to="/builder" className="text-[#BFA15F] hover:underline">builder seeking clean land opportunities</Link>, 
              we invite you to experience what ethical land wholesaling looks like in practice.
            </p>
          </div>
        </div>
      </section>
    </ScrollReveal>

      {/* Iron Sharpens Iron */}
      <section className="py-24 px-6 md:px-12 bg-[#0F0F10]" data-testid="iron-section">
        <ScrollReveal animation="reveal-up">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
              Iron Sharpens Iron
            </h2>
            <p className="text-[#A8A29E] text-lg mb-8 max-w-xl mx-auto">
              We believe in the power of Christian community. If you're a believer who wants to connect, pray together, or sharpen one another, we'd love to hear from you.
            </p>
            <ScriptureBlock
              verse="Iron sharpeneth iron; so a man sharpeneth the countenance of his friend."
              reference="Proverbs 27:17"
              className="inline-block text-left"
            />
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/prayer" aria-label="Visit prayer and community page">
                <Button 
                  size="lg" 
                  className="btn-enhanced bg-white text-[#0F0F10] hover:bg-[#F5F5F4] font-medium px-8"
                >
                  Visit Prayer & Community
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact" aria-label="Contact Steward Lots">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 font-medium px-8"
                >
                  Contact Us
                </Button>
            </Link>
          </div>
        </div>
      </ScrollReveal>
      </section>

      <Footer />
    </div>
  );
}
